<template>
  <SubscriptionNavigation
    @submit-clicked="onSubmit"
    :is-submitting-data="isSaveSubscriptionInProgress"
    :is-loading-data="isGetSubscriptionInProgress"
  >
    <div data-cy="contractor-information-page">
      <div class="text-center">
        <AtomText
          :text="t('subscription.contractorInformation.title')"
          font-style="font-semibold"
          font-size="text-xl"
          data-cy="contractor-information-page-title"
        />
      </div>

      <AtomText
        :text="t('subscription.contractorInformation.form.contractorTitle')"
        font-style="font-semibold"
        font-size="text-lg"
        class="mt-8"
        data-cy="contractor-information-contractor-title"
      />
      <MoleculeLabel
        :text="t('subscription.contractorInformation.form.clientType.label')"
        label-for="client-type"
        class="mt-8 mb-1 font-medium"
        data-cy="contractor-information-contractor-title"
      />
      <OrganismRadioGroupCard
        v-model="currentClientType"
        name="client-type"
        orientation="horizontal"
        :cards="clientTypeCards"
        :error-message="t('common.forms.required')"
        :has-error="isSubmitted && !currentClientType"
        data-cy="contractor-information-client-type-radio"
        class="mt-2"
      />

      <IndividualContractorFields
        v-if="currentClientType === EClientType.INDIVIDUAL"
        ref="contractorFormRef"
      />
      <CompanyContractorFields
        v-else-if="currentClientType === EClientType.COMPANY"
        ref="contractorFormRef"
      />
    </div>
  </SubscriptionNavigation>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useStepStore, useSubscriptionStore } from '@/stores';

import type { ClientType } from '@/types/subscription.type';

import CompanyContractorFields from '@/components/subscription/ContractorInformations/CompanyContractorFields.component.vue';
import IndividualContractorFields from '@/components/subscription/ContractorInformations/IndividualContractorFields.component.vue';
import SubscriptionNavigation from '@/components/SubscriptionNavigation.component.vue';

import { EClientType } from '@/constants/subscription.constants';

const { t } = useI18n();

const subscriptionStore = useSubscriptionStore();
const stepStore = useStepStore();

const { goToNextStep } = stepStore;

const { isSaveSubscriptionInProgress, isGetSubscriptionInProgress, currentSubscription } =
  storeToRefs(subscriptionStore);

const currentClientType = ref<ClientType | null>(
  currentSubscription.value.companyContractor !== null
    ? EClientType.COMPANY
    : currentSubscription.value.individualContractor !== null
      ? EClientType.INDIVIDUAL
      : null
);

const clientTypeCards = ref([
  {
    id: EClientType.INDIVIDUAL,
    value: EClientType.INDIVIDUAL,
    title: t('subscription.contractorInformation.form.clientType.value.individual'),
  },
  {
    id: EClientType.COMPANY,
    value: EClientType.COMPANY,
    title: t('subscription.contractorInformation.form.clientType.value.company'),
  },
]);

const isSubmitted = ref(false);

const contractorFormRef = ref<
  | InstanceType<typeof IndividualContractorFields>
  | InstanceType<typeof CompanyContractorFields>
  | null
>(null);

const onSubmit = async () => {
  isSubmitted.value = true;
  if (!currentClientType.value) {
    return;
  }
  const isSuccess = await contractorFormRef.value?.validateAndSubmit();
  if (isSuccess) {
    goToNextStep();
  }
};
</script>

<style scoped></style>
